import { EditContent } from "@/pages/editor";
import { HotSpot, PixelCoordinate } from "@xnav/types/src/common";
import { clickingOnCircle, drawCircle, isFrameHidden } from "./canvasFunctions";


const hotspotRadius = 5;

export function getHotspotIndexGivenTime(hotspot: HotSpot, time: number): number {
	let closestIndex = 0; // Closest index less than the given time
	for (let i = 0; i < hotspot.timeStamps.length; i++) {
		if (hotspot.timeStamps[i] <= time) closestIndex = i;
	}
	return closestIndex;
}

export function handlePotentialHotspotClick(
	click: PixelCoordinate,
	time: number, // Needs to be in tenths of seconds
	content: EditContent,
	setContent: (content: EditContent) => void
) {
	for (let i = 0; i < content.hotSpots.length; i++) {
		const hotspot = content.hotSpots[i]
		if (time < hotspot.timeStamps[0] || time > hotspot.timeStamps[hotspot.timeStamps.length - 1]) continue
		const currentIndex = getHotspotIndexGivenTime(hotspot, time);
		if (!clickingOnCircle(click, hotspot.points[currentIndex], hotspotRadius)) continue
		if (hotspot.timeStamps.includes(time)) {
			const newHotspot = hotspot
			newHotspot.points[currentIndex] = click
			const newHotspots = content.hotSpots
			newHotspots[i] = newHotspot
			setContent({
				...content,
				selectedMarkup: {
					index: i,
					subIndex: currentIndex,
					type: 'hotspot'
				},
				hotSpots: newHotspots
			})
			return true
		}
		const newHotspot = hotspot
		newHotspot.points.splice(currentIndex + 1, 0, click)
		newHotspot.timeStamps.splice(currentIndex + 1, 0, time)
		const newHotspots = content.hotSpots
		newHotspots[i] = newHotspot
		setContent({
			...content,
			selectedMarkup: {
				index: i,
				subIndex: currentIndex + 1,
				type: 'hotspot'
			},
			hotSpots: newHotspots
		})
		return true
	}
	return false
}

export function handleHotspotDrag(
	click: PixelCoordinate,
	content: EditContent,
	setContent: (content: EditContent) => void
) {
	if (!content.selectedMarkup || content.selectedMarkup.type !== 'hotspot') return
	const hotspot = content.hotSpots[content.selectedMarkup.index]
	const currentIndex = content.selectedMarkup.subIndex
	const newHotspot = hotspot
	newHotspot.points[currentIndex] = click
	const newHotspots = content.hotSpots
	newHotspots[content.selectedMarkup.index] = newHotspot
	setContent({
		...content,
		hotSpots: newHotspots
	})
}

export function drawHotspots(
	time: number,
	content: EditContent,
	ctx: CanvasRenderingContext2D
) {
	if (!content.selectedMarkup || content.selectedMarkup.type !== 'hotspot') {
		content.hotSpots.forEach((hotspot) => {
			if (time < hotspot.timeStamps[0] || time > hotspot.timeStamps[hotspot.timeStamps.length - 1]) return
			const currentIndex = getHotspotIndexGivenTime(hotspot, time)
			drawCircle(ctx, hotspot.points[currentIndex].x, hotspot.points[currentIndex].y, hotspotRadius, isFrameHidden(hotspot.hiddenFrames, time) ? '#888888' : hotspot.color)
		})
	} else {
		content.hotSpots.forEach((hotspot, index) => {
			if (time < hotspot.timeStamps[0] || time > hotspot.timeStamps[hotspot.timeStamps.length - 1]) return
			const currentIndex = getHotspotIndexGivenTime(hotspot, time)
			const innerColor = isFrameHidden(hotspot.hiddenFrames, time) ? '#888888' :
				index === content.selectedMarkup!.index ? '#ffffff88' : hotspot.color
			const outerColor = isFrameHidden(hotspot.hiddenFrames, time) ? '#666666' :
				index === content.selectedMarkup!.index ? hotspot.color : undefined
			drawCircle(ctx, hotspot.points[currentIndex].x, hotspot.points[currentIndex].y, hotspotRadius, innerColor, outerColor)
		})
	}
}

export function createNewHotSpot(click: PixelCoordinate, time: number): HotSpot {
	return {
		title: '',
		description: '',
		color: '#ff0000',
		points: [click, click],
		hiddenFrames: [],
		timeStamps: [time, time + 1],
		audios: []
	}
}

export function scaleHotSpot(hotspot: HotSpot, scale: number = 1): HotSpot {
	if (!hotspot) return hotspot;
	return {
		...hotspot,
		points: (hotspot.points || []).map(point => {
			return { x: point.x * scale, y: point.y * scale };
		})
	};
}

export function scaleHotSpots(hotspots: HotSpot[] = [], scale: number = 1): HotSpot[] {
	if (!hotspots || !Array.isArray(hotspots)) return [];
	return hotspots.map(hotspot => scaleHotSpot(hotspot, scale));
}
